import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { COLOR_SCHEMES } from '../../context/colorSchemes';
import useClickOutside from '../../hooks/useClickOutside';

const SchemeSwatch = ({ scheme }) => (
  <span aria-hidden="true" className="photon-scheme-disc relative block h-5 w-5 shrink-0 overflow-hidden rounded-full">
    <span className="absolute inset-y-0 left-0 w-1/2" style={{ backgroundColor: scheme.primary }} />
    <span className="absolute inset-y-0 right-0 w-1/2" style={{ backgroundColor: scheme.secondary }} />
  </span>
);

const ColorSchemePicker = ({ value, onChange, showLabels }) => {
  const selected = COLOR_SCHEMES.find(scheme => scheme.value === value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  useClickOutside(containerRef, () => setOpen(false), open);

  useEffect(() => {
    if (open) menuRef.current.querySelector('[role="menuitemradio"]').focus();
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    triggerRef.current.focus();
  };

  const handleMenuKeyDown = event => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      if (event.key === 'Escape') event.preventDefault();
      closeMenu();
      return;
    }
    const choices = [...menuRef.current.querySelectorAll('[role="menuitemradio"]')];
    const current = choices.indexOf(document.activeElement);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = choices.length - 1;
    else if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (current + 1) % choices.length;
    else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (current - 1 + choices.length) % choices.length;
    else return;
    event.preventDefault();
    choices[next].focus();
  };

  return (
    <div ref={containerRef} className={`photon-scheme-picker relative h-11 shrink-0 ${showLabels ? 'w-44 max-w-full' : 'w-14'}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Color scheme"
        title={selected.label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(previous => !previous)}
        onKeyDown={event => {
          if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={`photon-scheme-trigger relative flex h-full w-full items-center rounded-md ${showLabels ? 'gap-2 px-3' : 'justify-center'}`}
      >
        <SchemeSwatch scheme={selected} />
        {showLabels && <span className="photon-body-text min-w-0 flex-1 truncate text-left text-sm">{selected.label}</span>}
        <ChevronDown aria-hidden="true" className={`h-3 w-3 shrink-0 ${showLabels ? '' : 'absolute right-0'}`} />
      </button>
      {open && (
        <div id={menuId} ref={menuRef} role="menu" aria-label="Color scheme" aria-orientation="vertical" onKeyDown={handleMenuKeyDown} className="photon-scheme-menu absolute left-0 top-full z-50 mt-1 flex w-full flex-col rounded-md border shadow-md">
          {COLOR_SCHEMES.filter(scheme => scheme.value !== value).map((scheme, index) => (
            <button key={scheme.value} type="button" role="menuitemradio" aria-label={scheme.label} title={scheme.label} aria-checked={false} tabIndex={index === 0 ? 0 : -1} onClick={() => { onChange(scheme.value); closeMenu(); }} className={`photon-scheme-choice flex h-11 w-full shrink-0 items-center rounded-md ${showLabels ? 'gap-2 px-3' : 'justify-center'}`}>
              <SchemeSwatch scheme={scheme} />
              {showLabels && <span className="photon-body-text min-w-0 flex-1 truncate text-left text-sm">{scheme.label}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ColorSchemePicker;